import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, TextField, Typography, Box,
  CircularProgress, Alert, Paper, Container,
  ToggleButtonGroup, ToggleButton,
  Grid, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from "../../contexts/AuthContext";

const INDUSTRIES = [
  'Fashion & Apparel',
  'Beauty & Personal Care',
  'Food & Beverage',
  'Health & Fitness',
  'Technology & Software',
  'Education & E-learning',
  'Travel & Hospitality',
  'Entertainment & Media',
  'E-commerce & Retail',
  'Finance & Banking',
  'Automotive',
  'Real Estate',
  'Jewellery & Accessories',
  'Home Decor & Furniture',
  'Consumer Electronics',
  'FMCG',
  'Healthcare & Pharma',
  'Sports & Outdoors',
  'Agriculture & AgroTech',
  'Other',
];

const AddRoleForm = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roleType, setRoleType] = useState(null);

  // Shared fields
  const [bio, setBio] = useState('');

  // Brand-specific fields
  const [brandData, setBrandData] = useState({
    companyName: '',
    industry: '',
    website: '',
    location: '',
  });

  // Influencer-specific fields
  const [influencerData, setInfluencerData] = useState({
    firstName: '',
    lastName: '',
    experienceYears: 1,
    experienceMonths: 0,
  });

  const { userRoles, addRole } = useAuth();
  const navigate = useNavigate();

  const availableRoles = ['brand', 'influencer'].filter(role => !userRoles.includes(role));

  const handleRoleChange = (event, newRole) => {
    if (newRole !== null) setRoleType(newRole);
  };

  const handleBrandChange = (e) => {
    const { name, value } = e.target;
    setBrandData(prev => ({ ...prev, [name]: value }));
  };

  const handleInfluencerChange = (e) => {
    const { name, value } = e.target;
    setInfluencerData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!roleType) { setError('Please select a role type'); return; }

    if (roleType === 'brand' && !brandData.companyName.trim()) {
      setError('Company name is required');
      return;
    }
    if (roleType === 'influencer' && (!influencerData.firstName.trim() || !influencerData.lastName.trim())) {
      setError('First name and last name are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let payload;
      if (roleType === 'brand') {
        payload = {
          companyName: brandData.companyName.trim(),
          name: brandData.companyName.trim(), // backward compat — used as display name
          industry: brandData.industry,
          website: brandData.website.trim(),
          location: brandData.location.trim(),
          bio: bio.trim(),
        };
      } else {
        payload = {
          firstName: influencerData.firstName.trim(),
          lastName: influencerData.lastName.trim(),
          name: `${influencerData.firstName} ${influencerData.lastName}`.trim(),
          bio: bio.trim(),
          experienceYears: influencerData.experienceYears,
          experienceMonths: influencerData.experienceMonths,
        };
      }

      const success = await addRole(roleType, payload);
      if (success) navigate(`/${roleType}`);
    } catch (err) {
      setError(err.message || 'Failed to add role');
    } finally {
      setLoading(false);
    }
  };

  if (availableRoles.length === 0) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            You already have all available roles
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
            Go to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Add a New Role
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Select Role Type:
          </Typography>

          <ToggleButtonGroup
            value={roleType}
            exclusive
            onChange={handleRoleChange}
            aria-label="role type"
            fullWidth
            sx={{ mb: 3 }}
          >
            {availableRoles.includes('brand') && (
              <ToggleButton value="brand" aria-label="brand" sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <BusinessIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body1">Brand</Typography>
                  <Typography variant="body2" color="text.secondary">I represent a company</Typography>
                </Box>
              </ToggleButton>
            )}
            {availableRoles.includes('influencer') && (
              <ToggleButton value="influencer" aria-label="influencer" sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <PersonIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body1">Influencer</Typography>
                  <Typography variant="body2" color="text.secondary">I create content</Typography>
                </Box>
              </ToggleButton>
            )}
          </ToggleButtonGroup>

          {/* ── Brand fields ── */}
          {roleType === 'brand' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={brandData.companyName}
                  onChange={handleBrandChange}
                  required
                  placeholder="e.g. Acme Pvt. Ltd."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    name="industry"
                    value={brandData.industry}
                    label="Industry"
                    onChange={handleBrandChange}
                  >
                    {INDUSTRIES.map(ind => (
                      <MenuItem key={ind} value={ind}>{ind}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  value={brandData.website}
                  onChange={handleBrandChange}
                  placeholder="https://yourcompany.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={brandData.location}
                  onChange={handleBrandChange}
                  placeholder="e.g. Mumbai, Maharashtra"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="About the Brand"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Tell influencers what your brand is about…"
                />
              </Grid>
            </Grid>
          )}

          {/* ── Influencer fields ── */}
          {roleType === 'influencer' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={influencerData.firstName}
                  onChange={handleInfluencerChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={influencerData.lastName}
                  onChange={handleInfluencerChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Years of Experience</InputLabel>
                  <Select
                    name="experienceYears"
                    value={influencerData.experienceYears}
                    label="Years of Experience"
                    onChange={handleInfluencerChange}
                  >
                    {Array.from({ length: 15 }, (_, i) => i + 1).map((y) => (
                      <MenuItem key={y} value={y}>{y} {y === 1 ? 'year' : 'years'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Additional Months</InputLabel>
                  <Select
                    name="experienceMonths"
                    value={influencerData.experienceMonths}
                    label="Additional Months"
                    onChange={handleInfluencerChange}
                  >
                    {Array.from({ length: 12 }, (_, i) => i).map((m) => (
                      <MenuItem key={m} value={m}>{m} {m === 1 ? 'month' : 'months'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
            disabled={loading || !roleType}
          >
            {loading
              ? <CircularProgress size={24} />
              : `Add ${roleType ? roleType.charAt(0).toUpperCase() + roleType.slice(1) : ''} Role`}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddRoleForm;
